<template>
  <div v-if="modelValue" class="editor-overlay">
    <div class="editor-shell">
      <!-- Header -->
      <div class="editor-header">
        <div class="d-flex align-center" style="gap: 12px">
          <div class="header-icon"><v-icon size="18" color="#0a1628">mdi-book-open-page-variant-outline</v-icon></div>
          <div>
            <div class="header-title">{{ isNew ? 'Neue Anleitung' : 'Anleitung bearbeiten' }}</div>
            <div class="header-sub">Beschreibe Setup, Konfiguration und bekannte Eigenheiten</div>
          </div>
        </div>
        <div class="d-flex align-center" style="gap: 10px">
          <button class="bo" :disabled="saving" @click="$emit('update:modelValue', false)">
            <v-icon size="15">mdi-close</v-icon>
            <span>Abbrechen</span>
          </button>
          <button class="bp" :disabled="saving || !title.trim()" @click="save">
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-content-save-outline</v-icon>
            <span>Speichern</span>
          </button>
        </div>
      </div>

      <!-- Titel -->
      <div class="editor-titlebar">
        <label class="titlebar-label">Titel</label>
        <input
          v-model="title"
          type="text"
          class="titlebar-input"
          placeholder="z. B. Setup-Anleitung Hero-Banner"
          :disabled="saving"
        />
      </div>

      <!-- Editor -->
      <div class="editor-body">
        <QuillEditor
          ref="quillRef"
          v-model:content="content"
          content-type="html"
          theme="snow"
          :options="editorOptions"
          class="quill-light"
          @ready="onEditorReady"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import Quill from 'quill';
import BlotFormatter from 'quill-blot-formatter';

// Einmalig registrieren
if (!Quill.imports['modules/blotFormatter']) {
  Quill.register('modules/blotFormatter', BlotFormatter);
}

export default {
  name: 'GuideEditor',
  components: { QuillEditor },
  props: {
    modelValue: { type: Boolean, default: false },
    initialContent: { type: String, default: '' },
    initialTitle: { type: String, default: '' },
    isNew: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'save'],

  data() {
    return {
      content: '',
      title: '',
      editorOptions: {
        placeholder: 'Schreibe hier die Anleitung…',
        modules: {
          toolbar: {
            container: [
              [{ header: [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ color: [] }, { background: [] }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ align: [] }],
              ['link', 'image', 'video'],
              ['blockquote', 'code-block'],
              ['clean'],
            ],
            handlers: {
              image: this.imageHandler,
            },
          },
          blotFormatter: {},
        },
      },
    };
  },

  watch: {
    modelValue(val) {
      if (val) {
        this.content = this.initialContent || '';
        this.title = this.initialTitle || '';
      }
    },
  },

  beforeUnmount() {
    if (this._cleanupClick) this._cleanupClick();
  },

  methods: {
    onEditorReady() {
      const quill = this.$refs.quillRef?.getQuill();
      if (!quill) return;
      const root = quill.root;

      // Word-like: Klick unterhalb des letzten Inhalts → neue Zeile am Ende
      // und Cursor direkt dort platzieren. Ein Doppelklick macht dasselbe robust.
      const clickBelowContent = (e) => {
        if (e.target !== root) return; // nur wenn in den leeren Bereich geklickt
        const lastEl = root.lastElementChild;
        if (lastEl && e.clientY <= lastEl.getBoundingClientRect().bottom) return;
        this.focusAtEnd();
        e.preventDefault();
      };
      root.addEventListener('click', clickBelowContent);
      root.addEventListener('dblclick', clickBelowContent);
      this._cleanupClick = () => {
        root.removeEventListener('click', clickBelowContent);
        root.removeEventListener('dblclick', clickBelowContent);
      };
    },

    focusAtEnd() {
      const quill = this.$refs.quillRef?.getQuill();
      if (!quill) return;

      // Letzte Op ansehen: Wenn Bild direkt am Ende steht, brauchen wir noch ein \n
      const ops = quill.getContents().ops || [];
      const significantOps = [...ops].reverse().filter((op) => {
        // leere/whitespace-only \n ignorieren wir beim Check
        if (typeof op.insert === 'string' && /^\n*$/.test(op.insert)) return false;
        return true;
      });
      const lastSignificant = significantOps[0];
      const endsWithImage = lastSignificant && typeof lastSignificant.insert === 'object' && lastSignificant.insert.image;

      let length = quill.getLength();
      if (endsWithImage) {
        // Nach dem Bild noch einen Absatz einfügen, wenn nicht vorhanden
        quill.insertText(length, '\n', 'user');
        length = quill.getLength();
      }
      // Cursor ans Ende (vor das automatische Abschluss-\n)
      quill.focus();
      quill.setSelection(Math.max(0, length - 1), 0, 'user');
    },

    imageHandler() {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
          alert('Bild darf maximal 5 MB groß sein');
          return;
        }
        const reader = new FileReader();
        reader.onload = () => this.insertImage(reader.result);
        reader.readAsDataURL(file);
      };
    },

    insertImage(dataUrl) {
      const quill = this.$refs.quillRef?.getQuill();
      if (!quill) return;
      const range = quill.getSelection(true) || { index: quill.getLength() };
      // Bild einfügen
      quill.insertEmbed(range.index, 'image', dataUrl, 'user');
      // Danach IMMER einen Absatz einfügen, damit der Cursor nach dem Bild weiterschreiben kann
      quill.insertText(range.index + 1, '\n', 'user');
      quill.setSelection(range.index + 2, 0, 'user');
    },

    save() {
      if (!this.title.trim()) return;
      this.$emit('save', { title: this.title.trim(), content: this.content });
    },
  },
};
</script>

<style scoped>
.editor-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(10,22,40,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  animation: fadeIn 0.2s ease; padding: 24px;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

.editor-shell {
  width: 100%; max-width: 1200px; height: 100%; max-height: 90vh;
  background: white; border-radius: 16px; overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 64px rgba(10,22,40,0.25);
  animation: slideUp 0.25s cubic-bezier(0.16,1,0.3,1);
}
@keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.editor-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; border-bottom: 1px solid #eceef2;
  flex-shrink: 0; gap: 16px; flex-wrap: wrap;
}
.header-icon {
  width: 38px; height: 38px; border-radius: 10px;
  background: rgba(10,22,40,0.06);
  display: flex; align-items: center; justify-content: center;
}
.header-title { font-size: 16px; font-weight: 700; color: #0a1628; }
.header-sub { font-size: 12px; color: #888; margin-top: 2px; }

.bo {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px; border: 1px solid #eceef2; border-radius: 10px;
  background: white; font-size: 13px; font-weight: 600; color: #555;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.5; cursor: not-allowed; }

.bp {
  display: inline-flex; align-items: center; gap: 6px; min-width: 130px;
  padding: 9px 18px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; justify-content: center;
  font-family: inherit;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }

.editor-titlebar {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 24px;
  border-bottom: 1px solid #eceef2;
  background: white;
  flex-shrink: 0;
}
.titlebar-label {
  font-size: 12px; font-weight: 700; color: #0a1628;
  flex-shrink: 0;
}
.titlebar-input {
  flex: 1; min-width: 0;
  padding: 9px 12px;
  border: 1px solid #eceef2; border-radius: 8px;
  background: #f8f9fb; color: #0a1628;
  font-size: 13px; font-weight: 500; font-family: inherit;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.titlebar-input:focus {
  outline: none;
  border-color: #0a1628; background: white;
  box-shadow: 0 0 0 3px rgba(10,22,40,0.04);
}
.titlebar-input:disabled { opacity: 0.6; }

.editor-body {
  flex: 1; min-height: 0; display: flex; flex-direction: column;
  background: #fafbfc;
}
</style>

<style>
/* Quill Override — Light Theme angepasst */
.quill-light {
  display: flex; flex-direction: column; flex: 1; min-height: 0;
  background: white;
}
.quill-light .ql-toolbar.ql-snow {
  border: none !important;
  border-bottom: 1px solid #eceef2 !important;
  padding: 10px 16px;
  background: white;
  position: sticky; top: 0; z-index: 5;
  display: flex; flex-wrap: wrap; gap: 4px;
}
.quill-light .ql-container.ql-snow {
  border: none !important;
  flex: 1; min-height: 0;
  overflow-y: auto;
  background: white;
  font-size: 14px;
  color: #0a1628;
}
.quill-light .ql-editor {
  padding: 32px 48px;
  min-height: 100%;
  line-height: 1.6;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #0a1628 !important;
  background: white;
  cursor: text;
}
.quill-light .ql-editor:focus { outline: none; }
.quill-light .ql-editor *:not(img):not(video):not([style*="color"]) { color: inherit; }

.quill-light .ql-editor h1 { font-size: 28px; font-weight: 800; color: #0a1628 !important; margin-top: 24px; margin-bottom: 12px; letter-spacing: -0.3px; }
.quill-light .ql-editor h2 { font-size: 22px; font-weight: 700; color: #0a1628 !important; margin-top: 20px; margin-bottom: 10px; }
.quill-light .ql-editor h3 { font-size: 18px; font-weight: 700; color: #0a1628 !important; margin-top: 16px; margin-bottom: 8px; }
.quill-light .ql-editor p,
.quill-light .ql-editor li,
.quill-light .ql-editor span,
.quill-light .ql-editor strong,
.quill-light .ql-editor em { color: #0a1628; }
.quill-light .ql-editor p { margin-bottom: 10px; }
.quill-light .ql-editor a { color: #5B5BD6; text-decoration: none; }
.quill-light .ql-editor a:hover { text-decoration: underline; }
.quill-light .ql-editor blockquote {
  border-left: 3px solid #0a1628;
  padding: 6px 16px; margin: 14px 0;
  background: #f8f9fb; border-radius: 0 10px 10px 0;
  color: #555; font-style: italic;
}
.quill-light .ql-editor pre.ql-syntax,
.quill-light .ql-editor pre {
  background: #0a1628 !important; color: #eceef2 !important;
  padding: 16px !important; border-radius: 10px !important;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace !important;
  font-size: 13px !important; line-height: 1.55 !important;
  overflow-x: auto;
}
.quill-light .ql-editor img {
  border-radius: 10px;
  max-width: 100%;
  display: block; margin: 14px auto;
}
.quill-light .ql-editor ul, .quill-light .ql-editor ol { padding-left: 24px; margin-bottom: 10px; }

.quill-light .ql-editor.ql-blank::before {
  color: #bbb; font-style: normal; font-size: 14px;
  left: 48px; right: 48px;
}

/* Toolbar buttons */
.quill-light .ql-toolbar.ql-snow .ql-formats { margin-right: 6px; }
.quill-light .ql-toolbar button {
  border-radius: 6px;
  transition: background 0.15s;
}
.quill-light .ql-toolbar button:hover,
.quill-light .ql-toolbar .ql-picker-label:hover { background: #f8f9fb; }
.quill-light .ql-toolbar button.ql-active,
.quill-light .ql-toolbar .ql-picker-label.ql-active,
.quill-light .ql-toolbar .ql-picker-item.ql-selected { color: #0a1628 !important; background: #eceef2; }
.quill-light .ql-toolbar .ql-stroke { stroke: #555; }
.quill-light .ql-toolbar .ql-fill { fill: #555; }
.quill-light .ql-toolbar .ql-active .ql-stroke { stroke: #0a1628; }
.quill-light .ql-toolbar .ql-active .ql-fill { fill: #0a1628; }
.quill-light .ql-toolbar .ql-picker-label { color: #555; border-radius: 6px; }
.quill-light .ql-toolbar .ql-picker-options { background: white; border: 1px solid #eceef2 !important; border-radius: 8px; box-shadow: 0 8px 24px rgba(10,22,40,0.1); padding: 6px; }
.quill-light .ql-toolbar .ql-picker-item { border-radius: 4px; }
.quill-light .ql-toolbar .ql-picker-item:hover { background: #f8f9fb; }

/* Blot formatter resize handles — light */
.blot-formatter__proxy-image { background: rgba(10,22,40,0.04); }
</style>
